import { useTranslation } from 'react-i18next';
import { formatNumber } from '@/i18n/format';
import layout from '@/styles/layout.module.scss';
import styles from './satisfaction.module.scss';
import Card from '@/components/card/card';
import Meter from '@/components/meter/meter';
import StarRating from '@/components/star-rating/star-rating';
import { useScreenFilters } from '@/hooks/use-screen-filters/use-screen-filters';
import { useInsights } from '@/hooks/use-insights/use-insights';
import { useRatedSample } from '@/hooks/use-rated-sample/use-rated-sample';
import { useSurveys } from '@/hooks/use-surveys/use-surveys';
import { averageRating, formatScoreOutOfFive } from '@/utils/score';
import { QUOTE_SCAN_ROWS } from './satisfaction.constants';
import { countZeros, sampleCaption, toQuotes, toRatingRows } from './satisfaction.utils';

export function Satisfaction() {
  const { t } = useTranslation('satisfaction');
  const { scopedFilter, canQuery, isLoadingUserData } = useScreenFilters();

  const {
    insightsData,
    isLoading: isLoadingInsights,
    error: insightsError,
  } = useInsights(scopedFilter, canQuery);

  const {
    scores,
    sampled,
    atCeiling,
    arrived: sampleArrived,
    isLoading: isLoadingSample,
    error: sampleError,
  } = useRatedSample(scopedFilter, canQuery);

  const {
    surveysData,
    isLoading: isLoadingQuotes,
    error: quotesError,
  } = useSurveys({
    filtersParams: scopedFilter,
    paginationParams: { order_by: 'desc', page: 1, items_per_page: QUOTE_SCAN_ROWS },
    enabled: canQuery || !!scopedFilter.company_name,
  });

  // A gated-off query reports neither loading nor error, so the ladders below read this.
  const isResolvingViewer = isLoadingUserData;

  const satisfaction = insightsData?.overall_satisfaction;
  const rating = averageRating(satisfaction);

  const rows = toRatingRows(scores);
  const zeros = countZeros(scores);
  const ratedRows = rows.reduce((total, row) => total + row.count, 0);

  const quotes = toQuotes(surveysData?.surveys);
  // One page is read, so the empty case can only speak for that page — and only once the
  // payload has arrived, since a query the gate never sent reports neither state.
  const quotesMessage = quotesError
    ? t('quotes.messages.error')
    : isResolvingViewer || isLoadingQuotes
      ? t('quotes.messages.loading')
      : !surveysData || quotes.length
        ? null
        : surveysData.surveys_count
          ? t('quotes.messages.noWords', {
              count: Math.min(QUOTE_SCAN_ROWS, surveysData.surveys_count),
            })
          : t('quotes.messages.noRatings');

  const headlineMessage = insightsError
    ? t('rating.messages.headlineError')
    : isResolvingViewer || isLoadingInsights
      ? t('rating.messages.headlineLoading')
      : null;

  const distributionMessage = sampleError
    ? t('rating.messages.distributionError')
    : isResolvingViewer || isLoadingSample
      ? t('rating.messages.distributionLoading')
      : sampleArrived && !sampled
        ? t('rating.messages.distributionEmpty')
        : null;

  return (
    <div className={layout.page}>
      <div className={layout.gridNarrow}>
        <Card hint={t('rating.hint')} title={t('rating.title')}>
          {headlineMessage ? (
            <p className={layout.empty}>{headlineMessage}</p>
          ) : (
            <div className={styles.headline}>
              <b className={styles.headlineValue}>{formatScoreOutOfFive(rating)}</b>
              <span className={styles.headlineCaption}>
                {t('rating.caption', { count: satisfaction?.responses ?? 0 })}
              </span>
            </div>
          )}

          {distributionMessage ? (
            <p className={layout.empty}>{distributionMessage}</p>
          ) : (
            <>
              <div className={styles.rows}>
                {rows.map((row) => (
                  <Meter
                    ariaLabel={t('rating.stars', { count: row.stars })}
                    count={formatNumber(row.count)}
                    key={row.stars}
                    label={`${row.stars} ★`}
                    layout="row"
                    tone="rating"
                    value={row.percent}
                  />
                ))}
              </div>
              <p className={styles.sample}>
                {sampleCaption({
                  atCeiling,
                  rated: ratedRows,
                  responses: satisfaction?.responses ?? null,
                  sampled,
                  zeros,
                })}
              </p>
            </>
          )}
        </Card>

        <Card hint={t('quotes.hint')} title={t('quotes.title')}>
          {quotesMessage ? (
            <p className={layout.empty}>{quotesMessage}</p>
          ) : (
            quotes.map((quote) => (
              <div className={styles.quote} key={quote.id}>
                <p className={styles.quoteText}>{t('quotes.text', { text: quote.text })}</p>
                <div className={styles.quoteMeta}>
                  {/* A reply can arrive with no case subject resolved, and naming the
                      case then would be naming nothing. */}
                  <span>{quote.topic ? t('quotes.topic', { topic: quote.topic }) : ''}</span>
                  <StarRating score={quote.stars} />
                </div>
              </div>
            ))
          )}
        </Card>
      </div>
    </div>
  );
}
